import React, { Fragment, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PictureAsPdfIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  format,
  parseISO,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  subMonths
} from 'date-fns';
import libroDiarioService, { DeLiDiLineaDto, LibroDiarioListItem } from '../../services/libroDiarioService';
import PDFViewer from '../../components/shared/PDFViewer';
import { useAuth } from '../../contexts/AuthContext';

function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

/** Chips de período rápido; la API interpreta fecha hasta como día inclusivo. */
type QuickChip = 'hoy' | 'semana' | 'mes' | 'mesPasado' | '3meses' | '6meses';

const QUICK_CHIP_KEYS: QuickChip[] = ['hoy', 'semana', 'mes', 'mesPasado', '3meses', '6meses'];

function computeQuickRange(which: QuickChip): { desde: string; hasta: string } {
  const now = new Date();
  switch (which) {
    case 'hoy': {
      const d = format(now, 'yyyy-MM-dd');
      return { desde: d, hasta: d };
    }
    case 'semana':
      return {
        desde: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        hasta: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      };
    case 'mes':
      return {
        desde: format(startOfMonth(now), 'yyyy-MM-dd'),
        hasta: format(endOfMonth(now), 'yyyy-MM-dd')
      };
    case 'mesPasado': {
      const ref = subMonths(now, 1);
      return {
        desde: format(startOfMonth(ref), 'yyyy-MM-dd'),
        hasta: format(endOfMonth(ref), 'yyyy-MM-dd')
      };
    }
    case '3meses':
      return {
        desde: format(startOfDay(subMonths(now, 3)), 'yyyy-MM-dd'),
        hasta: format(endOfDay(now), 'yyyy-MM-dd')
      };
    case '6meses':
      return {
        desde: format(startOfDay(subMonths(now, 6)), 'yyyy-MM-dd'),
        hasta: format(endOfDay(now), 'yyyy-MM-dd')
      };
    default: {
      const _exhaustive: never = which;
      return _exhaustive;
    }
  }
}

/** Si las fechas coinciden exactamente con un período rápido (mismo resultado que computeQuickRange hoy). */
function findMatchingQuickChip(desde: string, hasta: string): QuickChip | null {
  for (const key of QUICK_CHIP_KEYS) {
    const r = computeQuickRange(key);
    if (desde === r.desde && hasta === r.hasta) return key;
  }
  return null;
}

function formatFecha(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    return isValid(d) ? format(d, 'dd/MM/yyyy') : iso;
  } catch {
    return iso;
  }
}

function fmtImporte(n: number): string {
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const AsientosDiario: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);
  const [items, setItems] = useState<LibroDiarioListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  /** Por defecto: mes actual; chip activo para reflejar el período rápido seleccionado. */
  const [fechaDesde, setFechaDesde] = useState(() => computeQuickRange('mes').desde);
  const [fechaHasta, setFechaHasta] = useState(() => computeQuickRange('mes').hasta);

  const presetActivo = useMemo(() => findMatchingQuickChip(fechaDesde, fechaHasta), [fechaDesde, fechaHasta]);
  const periodoPersonalizado = Boolean(fechaDesde && fechaHasta && presetActivo === null);
  const [nroAsientoFilter, setNroAsientoFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [confirmContabNro, setConfirmContabNro] = useState<number | null>(null);
  const [contabilizandoNro, setContabilizandoNro] = useState<number | null>(null);
  /** Fila expandida: líneas del asiento (cuenta / debe / haber). */
  const [expandedNro, setExpandedNro] = useState<number | null>(null);
  const [lineasPorAsiento, setLineasPorAsiento] = useState<Record<number, DeLiDiLineaDto[]>>({});
  const [cargandoLineasNro, setCargandoLineasNro] = useState<number | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;

  const applyQuickChip = useCallback((which: QuickChip) => {
    const r = computeQuickRange(which);
    setFechaDesde(r.desde);
    setFechaHasta(r.hasta);
    setPage(0);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const nro = nroAsientoFilter.trim() ? parseInt(nroAsientoFilter, 10) : undefined;
      const result = await libroDiarioService.getPaged({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm.trim() || undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        nroAsiento: Number.isFinite(nro) ? nro : undefined
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, nroAsientoFilter, enqueueSnackbar]);

  useEffect(() => {
    setExpandedNro(null);
    setLineasPorAsiento({});
    setCargandoLineasNro(null);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, nroAsientoFilter]);

  useEffect(() => {
    const t = setTimeout(() => fetchData(), 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = () => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handlePreviewPdf = async () => {
    try {
      setPdfLoading(true);
      const nro = nroAsientoFilter.trim() ? parseInt(nroAsientoFilter, 10) : undefined;
      const blob = await libroDiarioService.getPreviewPdfBlob({
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        searchTerm: searchTerm.trim() || undefined,
        nroAsiento: Number.isFinite(nro) ? nro : undefined
      });
      const safeBlob =
        blob instanceof Blob && blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e instanceof Error ? e.message : libroDiarioService.parseError(e), { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleConfirmContabilizar = async () => {
    if (confirmContabNro == null) return;
    try {
      setContabilizandoNro(confirmContabNro);
      await libroDiarioService.contabilizar(confirmContabNro);
      enqueueSnackbar('Asiento contabilizado', { variant: 'success' });
      setConfirmContabNro(null);
      await fetchData();
    } catch (e) {
      enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
    } finally {
      setContabilizandoNro(null);
    }
  };

  const toggleRowDetalle = async (nroAsiento: number) => {
    if (expandedNro === nroAsiento) {
      setExpandedNro(null);
      return;
    }
    setExpandedNro(nroAsiento);
    if (lineasPorAsiento[nroAsiento] !== undefined) {
      return;
    }
    try {
      setCargandoLineasNro(nroAsiento);
      const det = await libroDiarioService.getById(nroAsiento);
      setLineasPorAsiento((prev) => ({ ...prev, [nroAsiento]: det.lineas ?? [] }));
    } catch (e) {
      enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
      setExpandedNro(null);
    } finally {
      setCargandoLineasNro(null);
    }
  };

  const fmtLineaImporte = (v: number | null | undefined) => {
    if (v == null || Math.abs(Number(v)) < 0.000_001) return '—';
    return fmtImporte(Number(v));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Asientos del libro diario
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={handlePreviewPdf}
                disabled={pdfLoading}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                {pdfLoading ? 'Generando PDF…' : 'Libro Diario'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/contabilidad/asientos-diario/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                Nuevo asiento
              </Button>
            </Stack>
          </Stack>

          <Card sx={{ mb: 3, bgcolor: '#fafafa', borderRadius: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  label="Buscar en aclaración"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#1976d2' }} fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                />
                <TextField
                  size="small"
                  label="Nº asiento"
                  value={nroAsientoFilter}
                  onChange={(e) => {
                    setNroAsientoFilter(e.target.value.replace(/\D/g, ''));
                    setPage(0);
                  }}
                  sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                />
                <TextField
                  size="small"
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                />
                <TextField
                  size="small"
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Período rápido
                </Typography>
                {(
                  [
                    { key: 'hoy' as const, label: 'Hoy' },
                    { key: 'semana' as const, label: 'Semana actual' },
                    { key: 'mes' as const, label: 'Mes actual' },
                    { key: 'mesPasado' as const, label: 'Mes pasado' },
                    { key: '3meses' as const, label: 'Últ. 3 meses' },
                    { key: '6meses' as const, label: 'Últ. 6 meses' }
                  ] as const
                ).map(({ key, label }) => (
                  <Chip
                    key={key}
                    label={label}
                    size="small"
                    onClick={() => applyQuickChip(key)}
                    color={presetActivo === key ? 'primary' : 'default'}
                    variant={presetActivo === key ? 'filled' : 'outlined'}
                  />
                ))}
                <Chip
                  label="Personalizado"
                  size="small"
                  color={periodoPersonalizado ? 'primary' : 'default'}
                  variant={periodoPersonalizado ? 'filled' : 'outlined'}
                  title="Las fechas no coinciden con un período rápido; podés editarlas arriba."
                  sx={{ cursor: 'default' }}
                />
              </Stack>
            </CardContent>
          </Card>

          <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nº</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Aclaración</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Debe
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Haber
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Balanceado
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Estado
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9}>Cargando…</TableCell>
                </TableRow>
              )}
              {!loading &&
                items.map((row) => (
                  <Fragment key={row.nroAsiento}>
                    <TableRow
                      hover
                      selected={expandedNro === row.nroAsiento}
                      onClick={() => toggleRowDetalle(row.nroAsiento)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{row.nroAsiento}</TableCell>
                      <TableCell>{formatFecha(row.fechaAsiento)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.comprobante ?? '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>{(row.aclaracion || '').trim() || '—'}</TableCell>
                      <TableCell align="right">{fmtImporte(row.totalDebe)}</TableCell>
                      <TableCell align="right">{fmtImporte(row.totalHaber)}</TableCell>
                      <TableCell align="center">
                        {row.balanceado ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <Typography color="error" variant="caption">
                            {fmtImporte(row.diferencia)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={row.estaContabilizado ? 'Contabilizado' : 'Editable'}>
                          {row.estaContabilizado ? <LockIcon fontSize="small" color="action" /> : <LockOpenIcon fontSize="small" color="disabled" />}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
                          <Tooltip title="Visualizar">
                            <IconButton size="small" color="info" onClick={() => navigate(`/contabilidad/asientos-diario/${row.nroAsiento}`)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(!row.estaContabilizado || superAdmin) && (
                            <Tooltip title="Editar">
                              <IconButton size="small" color="primary" onClick={() => navigate(`/contabilidad/asientos-diario/${row.nroAsiento}`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!row.estaContabilizado && (
                            <Tooltip title="Contabilizar">
                              <span>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  disabled={contabilizandoNro === row.nroAsiento}
                                  onClick={() => setConfirmContabNro(row.nroAsiento)}
                                >
                                  {contabilizandoNro === row.nroAsiento ? (
                                    <CircularProgress color="secondary" size={18} thickness={5} />
                                  ) : (
                                    <VerifiedOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedNro === row.nroAsiento} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
                            {cargandoLineasNro === row.nroAsiento ? (
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <CircularProgress size={22} />
                                <Typography variant="body2" color="text.secondary">
                                  Cargando líneas…
                                </Typography>
                              </Stack>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: 'background.paper' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Cuenta</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      Debe
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      Haber
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(lineasPorAsiento[row.nroAsiento] ?? []).map((ln, idx) => (
                                    <TableRow key={`${row.nroAsiento}-${ln.nroLinea ?? idx}-${ln.nroId}-${idx}`}>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {ln.nroCuenta != null ? (
                                          <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <Tooltip title="Libro mayor de esta cuenta (período del filtro actual)">
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                aria-label="Ir a libro mayor"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const params = new URLSearchParams();
                                                  params.set('cuenta', String(ln.nroCuenta));
                                                  params.set('fechaDesde', fechaDesde);
                                                  params.set('fechaHasta', fechaHasta);
                                                  const nom = (ln.nomCuenta ?? '').trim();
                                                  if (nom) params.set('nomCuenta', nom);
                                                  navigate(`/contabilidad/libro-mayor?${params.toString()}`);
                                                }}
                                              >
                                                <AccountTreeIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Typography component="span" variant="body2">
                                              {`${ln.nroCuenta} — ${(ln.nomCuenta ?? '').trim() || '—'}`}
                                            </Typography>
                                          </Stack>
                                        ) : (
                                          '—'
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ maxWidth: 360 }}>{(ln.detalle ?? '').trim() || '—'}</TableCell>
                                      <TableCell align="right">{fmtLineaImporte(ln.debe)}</TableCell>
                                      <TableCell align="right">{fmtLineaImporte(ln.haber)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary">No hay asientos que coincidan.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Libro diario"
          documentId="libro-diario-preview"
          documentType="informeCliente"
          downloadFileName="Libro-diario.pdf"
        />
      ) : null}

      <Dialog open={confirmContabNro != null} onClose={() => setConfirmContabNro(null)}>
        <DialogTitle>Contabilizar asiento</DialogTitle>
        <DialogContent>
          ¿Confirma contabilizar el asiento {confirmContabNro ?? ''}? No podrá editarlo hasta que un SUPERADMIN descontabilice.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmContabNro(null)} disabled={contabilizandoNro != null}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleConfirmContabilizar} disabled={contabilizandoNro != null}>
            {contabilizandoNro != null ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AsientosDiario;
